import { Card, Col, Row, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { PLACEHOLDER_IMAGE } from "config/Constants";
import { errorHandler } from "api/api/errorHandler";
import { UTILS } from "utils";
import { API } from "api";
import TailwindButton from "components/Button";
import MetaShareButton from "components/Social-Media-Posting/MetaShareButton";
import { useGetCreatifyVideoById } from "./CreatifyAPI";
import { headerStyles, stepsEnum } from "./options";

const StepFour = ({
  cachedData,
  itemId,
  user,
  onNext = () => {},
  setLoading = () => {},
}) => {
  const router = useHistory();
  const [pauseVideo, setPauseVideo] = useState(false);
  let isNotCompleted = cachedData?.stepFour?.status === "done" ? false : true;
  const { data, isLoading: loading } = useGetCreatifyVideoById(
    cachedData?.stepFour?.status === "done" ? null : cachedData?.stepThree?.id,
    isNotCompleted
  );
  const { t } = useTranslation();

  if (["done", "failed"]?.includes(data?.status)) {
    isNotCompleted = false;
  }

  const handleDeleteCacheDoc = async (reset = false) => {
    try {
      const deleteTransientCachedData =
        await API.DELETE_TRANSIENT_CACHED_DATA_BY_CONTENT_ID({
          type: "creatifyApi",
          payloadContentId: cachedData?.stepOne?.id,
        });
      console.log("deleteTransientCachedData", deleteTransientCachedData);
      if (reset) {
        onNext(stepsEnum?.one, cachedData?.stepOne, false, true);
      }
    } catch (error) {}
  };
  
  const handleAttachVideo = async (event, isVideoPosted = false) => {
    try {
      setLoading(true);
      const payload = {
        itemId: itemId,
        brandId: user?.brandId,
        aiInfluencerAd: {
          thumbnail: data?.video_thumbnail || cachedData?.stepFour?.video_thumbnail ,
          video: data?.video_output || cachedData?.stepFour?.video_output,
          linkId: cachedData?.stepOne?.id,
        },
      }
      if(isVideoPosted) {
        payload.aiInfluencerAd.isAlreadyPosted = true
        payload.aiInfluencerAd.isAlreadyPostedOn = new Date().toISOString()
      }
      const result = await API.UPDATE_ITEM(payload);
      if (result?.success === 1) {
        await handleDeleteCacheDoc();
        UTILS.successNotification(t("Video Attached to Item Successfully"));
        router.goBack();
      } else {
        UTILS.errorNotification(errorHandler(result?.message));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      setLoading(false);
    }
  };

  const videoRef = useRef(null);

  // Control video playback
  useEffect(() => {
    if (videoRef.current) {
      if (pauseVideo) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [pauseVideo]);

  return (
    <>
      <Card title={t("AI Influencer Video Preview")} headStyle={headerStyles}>
        {data?.failed_reason ? (
          <div className="mb-4 flex justify-center items-center flex-col gap-4">
            <Typography.Title level={4}>{data?.failed_reason}</Typography.Title>
            <TailwindButton
              variant="danger"
              text={t("Retry Again")}
              onClick={() => handleDeleteCacheDoc(true)}
            />
          </div>
        ) : loading || isNotCompleted ? (
          <div className="mb-4 text-center h-80 flex items-center justify-center flex-col gap-4">
            <Spin size="large" tip={t("AI Influencer Video Processing")} />
            <Typography.Title level={5} className="text-red-400">
              {t(
                "You can close this window now, come back later to see the result"
              )}
            </Typography.Title>
          </div>
        ) : (
          <>
            <Row gutter={16}>
              <Col span={12} className="h-full">
                <Card title={t("Video Preview")} bordered={false}>
                  <video
                    ref={videoRef}
                    src={data?.video_output || cachedData?.stepFour?.video_output}
                    poster={
                      loading
                        ? PLACEHOLDER_IMAGE
                        : data?.video_thumbnail ||
                          cachedData?.stepFour?.video_thumbnail ||
                          PLACEHOLDER_IMAGE
                    }
                    controls
                    autoPlay={pauseVideo ? false : true}
                    loop={true}
                    muted={pauseVideo}
                    className="w-full h-full"
                  >
                    Your browser does not support the video tag.
                  </video>
                </Card>
              </Col>
            <Col span={12} className="h-full">
                <Card title={t("Video Thumbnail")} bordered={false}>
                  <img
                    src={
                      loading
                        ? PLACEHOLDER_IMAGE
                        : data?.video_thumbnail ||
                          cachedData?.stepFour?.video_thumbnail
                    }
                    alt={t("Video Thumbnail Not Available")}
                  />
                </Card>
              </Col>
            </Row>
            <div className="flex gap-4 mt-4">
              <TailwindButton
                text={t("Save Video")}
                disabled={loading}
                onClick={(e) => handleAttachVideo(e, false)}
              />
              {/* TODO: Add Next & Post and Reset buttons when functionality is implemented */}
              <MetaShareButton
                setLoading={setLoading}
                loading={loading}
                setPauseVideo={setPauseVideo}
                mediaUrl={data?.video_output || cachedData?.stepFour?.video_output}
                mediaType="video"
                onSave={handleAttachVideo}
                isPost={cachedData?.stepFour?.isAlreadyPosted || false}
              />
              <TailwindButton
                variant="danger"
                onClick={() => handleDeleteCacheDoc(true)}
                text={t("Reset")}
                disabled={loading}
              />
            </div>
          </>
        )}
      </Card>
    </>
  );
};

export default StepFour;
